import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.korslovo.crossword');
import { OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Service } from "src/libs/portal/season/service";
import { RU } from "src/libs/portal/korslovo/i18n";

type Phase = 'loading' | 'playing' | 'review' | 'result';

interface ClueItem {
    number: number;
    clue: string;
    length: number;
    row: number;
    col: number;
    word_id: string;
    is_new: boolean;
}

interface GridCell {
    active: boolean;
    number?: number;
}

interface LetterBox {
    value: string;
    cellIndex: number;
    revealed: boolean;
    locked: boolean;
}

@Component({
    selector: 'wiz-page-korslovo-crossword',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.korslovo.crossword/view.scss */
@charset "UTF-8";
:host {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* ─── Cursor blink on active letter box ─── */
.cw-cursor-blink {
  animation: cw-blink 1s step-end infinite;
}

@keyframes cw-blink {
  0%, 100% {
    box-shadow: inset 0 -2px 0 0 rgb(14, 165, 233);
  }
  50% {
    box-shadow: inset 0 -2px 0 0 transparent;
  }
}
/* ─── Wrong answer shake ─── */
.cw-shake {
  animation: cw-shake 0.5s ease-in-out;
}

@keyframes cw-shake {
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-4px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(4px);
  }
}
/* ─── Correct answer pop ─── */
.cw-pop {
  animation: cw-pop 0.4s ease-out;
}

@keyframes cw-pop {
  0% {
    transform: scale(1);
  }
  40% {
    transform: scale(1.06);
  }
  100% {
    transform: scale(1);
  }
}
/* ─── Sticky bottom input panel ─── */
.cw-input-panel {
  position: sticky;
  bottom: 0;
  z-index: 10;
}

/* Prevent iOS touch-action on crossword grid */
.cw-grid {
  touch-action: manipulation;
  -webkit-touch-callout: none;
}

/* Visible Korean input field */
.cw-korean-input {
  flex: 1;
  max-width: 200px;
  height: 40px;
  font-size: 18px;
  text-align: center;
  border: 2px solid #d1d5db;
  border-radius: 12px;
  outline: none;
  background: #f9fafb;
  color: #18181b;
  padding: 0 12px;
  transition: border-color 0.15s;
}
.cw-korean-input:focus {
  border-color: #0ea5e9;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
}
.cw-korean-input::placeholder {
  color: #a1a1aa;
  font-size: 14px;
}`],
})
export class PageKorslovoCrosswordComponent implements OnInit, OnDestroy {
    @ViewChild('korInput') korInputRef!: ElementRef<HTMLInputElement>;
    public ru = RU;
    public phase: Phase = 'loading';

    // Puzzle data
    public rows: number = 0;
    public cols: number = 0;
    public cells: (GridCell | null)[][] = [];
    public acrossClues: ClueItem[] = [];
    public downClues: ClueItem[] = [];
    public answers: any = {};
    public totalWords: number = 0;
    public newWordsCount: number = 0;
    public errorMessage: string = '';

    // User input
    public userGrid: string[][] = [];

    // Selection
    public selectedRow: number = -1;
    public selectedCol: number = -1;
    public direction: 'across' | 'down' = 'across';
    public activeClueKey: string = '';

    // Letter box input
    public letterBoxes: LetterBox[] = [];
    public activeBoxIndex: number = 0;

    // Game state
    public timerSeconds: number = 0;
    public hintsUsed: number = 0;
    public correctWords: Set<string> = new Set();
    public revealedCells: Set<string> = new Set();

    // Word feedback animation
    public wordFeedback: 'none' | 'correct' | 'wrong' = 'none';

    // Review state
    public wrongWordKeys: Set<string> = new Set();

    // Result
    public resultCorrect: number = 0;
    public resultTotal: number = 0;
    public wrongWords: { korean: string; clue: string; direction: string }[] = [];

    // XP
    public xpResult: any = null;
    public showXpAnimation: boolean = false;
    public showLevelUp: boolean = false;

    // Word IDs for history tracking
    private puzzleWordIds: string[] = [];

    private timerInterval: any = null;
    private lessonId: string = '';
    private crosswordMode: string = '';
    private feedbackTimeout: any = null;
    private _transitioning: boolean = false;
    constructor(@Inject( Service) public service: Service) { }

    public async ngOnInit() {
        await this.service.init();
        this.lessonId = WizRoute.segment.lessonId || '';

        if (this.lessonId === 'random') {
            const urlParams = new URLSearchParams(window.location.search);
            this.crosswordMode = urlParams.get('mode') || 'learned';
        }

        if (this.lessonId) {
            await this.generatePuzzle();
        }
        await this.service.render();
    }

    ngOnDestroy() {
        this.stopTimer();
        if (this.feedbackTimeout) clearTimeout(this.feedbackTimeout);
    }

    // ─── Generate ───
    public async generatePuzzle() {
        this.phase = 'loading';
        this.errorMessage = '';
        await this.service.render();

        let code: number, data: any;
        if (this.crosswordMode) {
            ({ code, data } = await wiz.call('generate_by_mode', { mode: this.crosswordMode }));
        } else {
            ({ code, data } = await wiz.call('generate', { lesson_id: this.lessonId }));
        }
        if (code !== 200 || data.error) {
            this.errorMessage = data?.message || 'error';
            this.phase = 'loading';
            await this.service.render();
            return;
        }

        this.rows = data.rows;
        this.cols = data.cols;
        this.cells = data.cells;
        this.acrossClues = data.across || [];
        this.downClues = data.down || [];
        this.answers = data.answers || {};
        this.totalWords = data.total_words || 0;
        this.newWordsCount = data.new_words_count || 0;

        // Collect word IDs for history tracking
        const wordIdSet = new Set<string>();
        for (const clue of this.acrossClues) { if (clue.word_id) wordIdSet.add(clue.word_id); }
        for (const clue of this.downClues) { if (clue.word_id) wordIdSet.add(clue.word_id); }
        this.puzzleWordIds = Array.from(wordIdSet);

        this.userGrid = [];
        for (let r = 0; r < this.rows; r++) {
            const row: string[] = [];
            for (let c = 0; c < this.cols; c++) {
                row.push('');
            }
            this.userGrid.push(row);
        }

        this.selectedRow = -1;
        this.selectedCol = -1;
        this.direction = 'across';
        this.activeClueKey = '';
        this.letterBoxes = [];
        this.activeBoxIndex = 0;
        this.timerSeconds = 0;
        this.hintsUsed = 0;
        this.correctWords = new Set();
        this.revealedCells = new Set();
        this.wordFeedback = 'none';
        this.phase = 'playing';

        // Auto-select first clue
        this.selectFirstUnanswered();
        this.startTimer();
        await this.service.render();

    }

    // ─── Timer ───
    private startTimer() {
        this.stopTimer();
        this.timerInterval = setInterval(() => {
            this.timerSeconds++;
            this.service.render();
        }, 1000);
    }

    private stopTimer() {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
            this.timerInterval = null;
        }
    }

    public formatTime(s: number): string {
        const m = Math.floor(s / 60);
        const sec = s % 60;
        return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
    }

    // ─── Cell interaction ───
    public isActiveCell(r: number, c: number): boolean {
        return this.cells[r]?.[c]?.active === true;
    }

    public cellKey(r: number, c: number): string {
        return `${r}_${c}`;
    }

    public selectCell(r: number, c: number) {
        if (!this.isActiveCell(r, c)) return;

        // Flush Korean IME and sync current word before switching
        this.flushInput();

        if (this.selectedRow === r && this.selectedCol === c) {
            this.direction = this.direction === 'across' ? 'down' : 'across';
        } else {
            this.selectedRow = r;
            this.selectedCol = c;
        }

        this.updateActiveClue();
        this.updateLetterBoxes();
        this.service.render();
        this.deferFocusInput();
    }

    // Tap anywhere in game area → refocus input
    public tapToFocus() {
        if (this.phase === 'playing' && this.activeClueKey) {
            this.focusInput();
        }
    }

    public focusInput() {
        this._transitioning = false;
        const input = this.korInputRef?.nativeElement;
        if (input) {
            // Build pre-fill string ONLY from editable boxes of current word
            const editableIndices: number[] = [];
            let val = '';
            for (let i = 0; i < this.letterBoxes.length; i++) {
                const box = this.letterBoxes[i];
                if (box.locked || box.revealed) continue;
                editableIndices.push(i);
            }
            // Collect contiguous filled values from the start
            for (const idx of editableIndices) {
                if (this.letterBoxes[idx].value) val += this.letterBoxes[idx].value;
                else break;
            }
            input.value = val;
            input.focus();
        }
    }

    private flushInput() {
        this._transitioning = true;
        const input = this.korInputRef?.nativeElement;
        if (input) {
            // Capture the final IME-composed value BEFORE blur clears it
            const val = input.value || '';
            // Process the final value into letterBoxes (same logic as onInputEvent)
            if (this.letterBoxes.length > 0) {
                const editableIndices: number[] = [];
                for (let i = 0; i < this.letterBoxes.length; i++) {
                    if (!this.letterBoxes[i].locked && !this.letterBoxes[i].revealed) {
                        editableIndices.push(i);
                    }
                }
                const trimmed = val.substring(0, editableIndices.length);
                for (let j = 0; j < editableIndices.length; j++) {
                    const idx = editableIndices[j];
                    this.letterBoxes[idx].value = j < trimmed.length ? trimmed[j] : '';
                }
            }
            input.blur();
            input.value = '';
        }
        if (this.activeClueKey) {
            this.syncBoxesToGrid();
        }
    }

    private deferFocusInput() {
        setTimeout(() => { this.focusInput(); }, 50);
    }

    private updateActiveClue() {
        const clues = this.direction === 'across' ? this.acrossClues : this.downClues;
        for (const clue of clues) {
            if (this.cellBelongsToClue(this.selectedRow, this.selectedCol, clue, this.direction)) {
                this.activeClueKey = `${clue.number}_${this.direction}`;
                return;
            }
        }
        const altDir = this.direction === 'across' ? 'down' : 'across';
        const altClues = altDir === 'across' ? this.acrossClues : this.downClues;
        for (const clue of altClues) {
            if (this.cellBelongsToClue(this.selectedRow, this.selectedCol, clue, altDir)) {
                this.direction = altDir;
                this.activeClueKey = `${clue.number}_${altDir}`;
                return;
            }
        }
    }

    private cellBelongsToClue(r: number, c: number, clue: ClueItem, dir: string): boolean {
        if (dir === 'across') {
            return r === clue.row && c >= clue.col && c < clue.col + clue.length;
        } else {
            return c === clue.col && r >= clue.row && r < clue.row + clue.length;
        }
    }

    public isHighlighted(r: number, c: number): boolean {
        if (this.selectedRow < 0) return false;
        const parts = this.activeClueKey.split('_');
        if (parts.length < 2) return false;
        const num = parseInt(parts[0]);
        const dir = parts.slice(1).join('_') as string;
        const clues = dir === 'across' ? this.acrossClues : this.downClues;
        const clue = clues.find(cl => cl.number === num);
        if (!clue) return false;
        return this.cellBelongsToClue(r, c, clue, dir);
    }

    public isSelected(r: number, c: number): boolean {
        return this.selectedRow === r && this.selectedCol === c;
    }

    public isCellCorrectWord(r: number, c: number): boolean {
        const allClues = [...this.acrossClues.map(c => ({...c, dir: 'across'})), ...this.downClues.map(c => ({...c, dir: 'down'}))];
        for (const clue of allClues) {
            const key = `${clue.number}_${clue.dir}`;
            if (this.correctWords.has(key) && this.cellBelongsToClue(r, c, clue, clue.dir)) {
                return true;
            }
        }
        return false;
    }

    public isCellWrongWord(r: number, c: number): boolean {
        if (this.wrongWordKeys.size === 0) return false;
        const allClues = [...this.acrossClues.map(c => ({...c, dir: 'across'})), ...this.downClues.map(c => ({...c, dir: 'down'}))];
        for (const clue of allClues) {
            const key = `${clue.number}_${clue.dir}`;
            if (this.wrongWordKeys.has(key) && this.cellBelongsToClue(r, c, clue, clue.dir)) {
                return true;
            }
        }
        return false;
    }

    public getCellClass(r: number, c: number): string {
        const base = 'relative border border-zinc-300 flex items-center justify-center cursor-pointer transition-colors';
        if (!this.isActiveCell(r, c)) return base + ' bg-white';
        const key = this.cellKey(r, c);
        const isRevealed = this.revealedCells.has(key);
        const isCorrectWord = this.isCellCorrectWord(r, c);
        const isWrongWord = this.isCellWrongWord(r, c);

        if (this.phase === 'review') {
            if (isCorrectWord) return base + ' bg-emerald-50 border-emerald-400';
            if (isWrongWord) return base + ' bg-red-50 border-red-400';
            return base + ' bg-white';
        }

        if (isCorrectWord) return base + ' bg-amber-50 border-amber-300';
        if (this.isSelected(r, c)) return base + ' bg-sky-300 border-sky-400';
        if (this.isHighlighted(r, c)) return base + (isRevealed ? ' bg-amber-100' : ' bg-sky-100');
        if (isRevealed) return base + ' bg-amber-50';
        return base + ' bg-white';
    }

    // ─── Letter Box Input System ───
    private isCellLocked(r: number, c: number): boolean {
        const allClues = [...this.acrossClues.map(c => ({...c, dir: 'across'})), ...this.downClues.map(c => ({...c, dir: 'down'}))];
        for (const clue of allClues) {
            const key = `${clue.number}_${clue.dir}`;
            if (this.correctWords.has(key) && key !== this.activeClueKey) {
                if (this.cellBelongsToClue(r, c, clue, clue.dir)) return true;
            }
        }
        return false;
    }

    private updateLetterBoxes() {
        const clue = this.getActiveClueObj();
        if (!clue) {
            this.letterBoxes = [];
            return;
        }
        const dir = this.activeClueKey.split('_').slice(1).join('_');
        const dr = dir === 'down' ? 1 : 0;
        const dc = dir === 'across' ? 1 : 0;

        this.letterBoxes = [];
        for (let i = 0; i < clue.length; i++) {
            const r = clue.row + dr * i;
            const c = clue.col + dc * i;
            this.letterBoxes.push({
                value: this.userGrid[r]?.[c] || '',
                cellIndex: i,
                revealed: this.revealedCells.has(this.cellKey(r, c)),
                locked: this.isCellLocked(r, c)
            });
        }

        this.activeBoxIndex = this.findNextEmptyBox(0);
        this.wordFeedback = 'none';
    }

    private findNextEmptyBox(from: number): number {
        for (let i = from; i < this.letterBoxes.length; i++) {
            if (!this.letterBoxes[i].value && !this.letterBoxes[i].revealed && !this.letterBoxes[i].locked) return i;
        }
        // All filled — find last editable box
        for (let i = this.letterBoxes.length - 1; i >= 0; i--) {
            if (!this.letterBoxes[i].revealed && !this.letterBoxes[i].locked) return i;
        }
        return Math.min(from, this.letterBoxes.length - 1);
    }

    public onBoxClick(index: number) {
        if (this.letterBoxes[index]?.revealed || this.letterBoxes[index]?.locked) return;
        this.activeBoxIndex = index;
        this.focusInput();
        this.service.render();
    }

    public getBoxClass(box: LetterBox, index: number): string {
        const base = 'w-10 h-11 border-2 rounded-lg flex items-center justify-center text-base font-bold transition-all select-none';
        if (box.locked) return base + ' border-emerald-300 bg-emerald-50 text-emerald-700';
        if (box.revealed) return base + ' border-amber-400 bg-amber-50 text-amber-700';
        if (this.wordFeedback === 'correct') return base + ' border-emerald-400 bg-emerald-50 text-emerald-700';
        if (this.wordFeedback === 'wrong') return base + ' border-red-400 bg-red-50 text-red-600';
        if (index === this.activeBoxIndex) return base + ' border-sky-500 bg-sky-50 ring-2 ring-sky-200 cw-cursor-blink';
        if (box.value) return base + ' border-zinc-400 bg-white text-zinc-900';
        return base + ' border-zinc-200 bg-zinc-50 text-zinc-300';
    }

    // ─── Input: map input field value directly to editable boxes (iOS-safe, no composition events) ───
    public onInputEvent(e: any) {
        if (this._transitioning) return;
        if (this.selectedRow < 0 || this.selectedCol < 0) return;
        if (this.wordFeedback !== 'none') return;

        const input = e.target as HTMLInputElement;
        const val = input?.value || '';

        // Get editable box indices
        const editableIndices: number[] = [];
        for (let i = 0; i < this.letterBoxes.length; i++) {
            if (!this.letterBoxes[i].locked && !this.letterBoxes[i].revealed) {
                editableIndices.push(i);
            }
        }

        // Limit input length to number of editable boxes
        const maxLen = editableIndices.length;
        if (val.length > maxLen) {
            input.value = val.substring(0, maxLen);
        }
        const trimmed = val.substring(0, maxLen);

        // Distribute characters to editable boxes
        for (let j = 0; j < editableIndices.length; j++) {
            const idx = editableIndices[j];
            this.letterBoxes[idx].value = j < trimmed.length ? trimmed[j] : '';
        }

        this.syncBoxesToGrid();

        // Update active box index
        const filled = Math.min(trimmed.length, editableIndices.length);
        this.activeBoxIndex = filled < editableIndices.length
            ? editableIndices[filled]
            : editableIndices[editableIndices.length - 1] ?? 0;

        this.service.render();
    }

    public onKeyDown(e: KeyboardEvent) {
        if (this.phase !== 'playing') return;
        if (this.wordFeedback !== 'none') return;

        if (e.key === 'Enter') {
            e.preventDefault();
            this.checkCurrentWord();
            return;
        }
        if (e.key === 'Tab') {
            e.preventDefault();
            this.flushInput();
            if (e.shiftKey) {
                this.moveToPrevWord();
            } else {
                this.moveToNextWord();
            }
            this.service.render();
            this.deferFocusInput();
        }
    }

    private setBoxValue(index: number, value: string) {
        if (index >= 0 && index < this.letterBoxes.length && !this.letterBoxes[index].revealed && !this.letterBoxes[index].locked) {
            this.letterBoxes[index].value = value;
        }
    }

    private syncBoxesToGrid() {
        const clue = this.getActiveClueObj();
        if (!clue) return;
        const dir = this.activeClueKey.split('_').slice(1).join('_');
        const dr = dir === 'down' ? 1 : 0;
        const dc = dir === 'across' ? 1 : 0;

        for (let i = 0; i < this.letterBoxes.length; i++) {
            const r = clue.row + dr * i;
            const c = clue.col + dc * i;
            if (!this.revealedCells.has(this.cellKey(r, c))) {
                this.userGrid[r][c] = this.letterBoxes[i].value;
            }
        }
    }

    private getActiveClueObj(): ClueItem | null {
        if (!this.activeClueKey) return null;
        const parts = this.activeClueKey.split('_');
        const num = parseInt(parts[0]);
        const dir = parts.slice(1).join('_');
        const clues = dir === 'across' ? this.acrossClues : this.downClues;
        return clues.find(c => c.number === num) || null;
    }

    // ─── Check & feedback ───
    private isAllBoxesFilled(): boolean {
        return this.letterBoxes.every(b => b.value || b.revealed || b.locked);
    }

    public get allBoxesFilled(): boolean {
        return this.letterBoxes.length > 0 && this.isAllBoxesFilled();
    }

    public checkCurrentWord() {
        if (this.wordFeedback !== 'none') return;
        if (!this.activeClueKey) return;
        // Flush Korean IME composition before transitioning
        this.flushInput();
        // If all grid cells are filled, auto-submit
        if (this.allGridFilled) {
            this.submitAll();
            return;
        }
        this.moveToNextWord();
        this.service.render();
        this.deferFocusInput();
    }

    public get allGridFilled(): boolean {
        for (let r = 0; r < this.rows; r++) {
            for (let c = 0; c < this.cols; c++) {
                if (this.cells[r]?.[c]?.active && !this.userGrid[r][c]) return false;
            }
        }
        return this.rows > 0;
    }

    public get filledWordsCount(): number {
        const allClues = [...this.acrossClues.map(c => ({...c, dir: 'across'})), ...this.downClues.map(c => ({...c, dir: 'down'}))];
        let count = 0;
        for (const clue of allClues) {
            const dr = clue.dir === 'down' ? 1 : 0;
            const dc = clue.dir === 'across' ? 1 : 0;
            let allFilled = true;
            for (let i = 0; i < clue.length; i++) {
                if (!this.userGrid[clue.row + dr * i]?.[clue.col + dc * i]) { allFilled = false; break; }
            }
            if (allFilled) count++;
        }
        return count;
    }

    // ─── Navigation ───
    private selectFirstUnanswered() {
        const allClues = [...this.acrossClues.map(c => ({...c, dir: 'across'})), ...this.downClues.map(c => ({...c, dir: 'down'}))];
        for (const clue of allClues) {
            const key = `${clue.number}_${clue.dir}`;
            if (!this.correctWords.has(key)) {
                this.direction = clue.dir as ('across' | 'down');
                this.selectedRow = clue.row;
                this.selectedCol = clue.col;
                this.activeClueKey = key;
                this.updateLetterBoxes();
                return;
            }
        }
    }

    private moveToNextWord() {
        const allClues = [...this.acrossClues.map(c => ({...c, dir: 'across'})), ...this.downClues.map(c => ({...c, dir: 'down'}))];
        const currentIdx = allClues.findIndex(c => `${c.number}_${c.dir}` === this.activeClueKey);

        // First pass: find next word that has empty cells
        for (let i = 1; i <= allClues.length; i++) {
            const idx = (currentIdx + i) % allClues.length;
            const next = allClues[idx];
            const key = `${next.number}_${next.dir}`;
            if (this.correctWords.has(key)) continue;
            if (this.isWordFilled(next)) continue;
            this.direction = next.dir as ('across' | 'down');
            this.selectedRow = next.row;
            this.selectedCol = next.col;
            this.activeClueKey = key;
            this.updateLetterBoxes();
            return;
        }

        // Second pass: if all non-correct words are filled, go to any non-correct word
        for (let i = 1; i <= allClues.length; i++) {
            const idx = (currentIdx + i) % allClues.length;
            const next = allClues[idx];
            const key = `${next.number}_${next.dir}`;
            if (!this.correctWords.has(key)) {
                this.direction = next.dir as ('across' | 'down');
                this.selectedRow = next.row;
                this.selectedCol = next.col;
                this.activeClueKey = key;
                this.updateLetterBoxes();
                return;
            }
        }
    }

    private isWordFilled(clue: any): boolean {
        const dr = clue.dir === 'down' ? 1 : 0;
        const dc = clue.dir === 'across' ? 1 : 0;
        for (let i = 0; i < clue.length; i++) {
            if (!this.userGrid[clue.row + dr * i]?.[clue.col + dc * i]) return false;
        }
        return true;
    }

    private moveToPrevWord() {
        const allClues = [...this.acrossClues.map(c => ({...c, dir: 'across'})), ...this.downClues.map(c => ({...c, dir: 'down'}))];
        const currentIdx = allClues.findIndex(c => `${c.number}_${c.dir}` === this.activeClueKey);
        for (let i = 1; i <= allClues.length; i++) {
            const idx = (currentIdx - i + allClues.length) % allClues.length;
            const prev = allClues[idx];
            const key = `${prev.number}_${prev.dir}`;
            if (!this.correctWords.has(key)) {
                this.direction = prev.dir as ('across' | 'down');
                this.selectedRow = prev.row;
                this.selectedCol = prev.col;
                this.activeClueKey = key;
                this.updateLetterBoxes();
                return;
            }
        }
    }

    // ─── Word completion check (for submit all) ───
    private checkAllWords() {
        this.syncBoxesToGrid();
        const allClues = [...this.acrossClues.map(c => ({...c, dir: 'across'})), ...this.downClues.map(c => ({...c, dir: 'down'}))];

        for (const clue of allClues) {
            const key = `${clue.number}_${clue.dir}`;
            if (this.correctWords.has(key)) continue;

            const dr = clue.dir === 'down' ? 1 : 0;
            const dc = clue.dir === 'across' ? 1 : 0;
            let word = '';
            let allFilled = true;
            for (let i = 0; i < clue.length; i++) {
                const ch = this.userGrid[clue.row + dr * i]?.[clue.col + dc * i] || '';
                if (!ch) { allFilled = false; break; }
                word += ch;
            }
            if (!allFilled) continue;

            const correct = this.answers[key];
            if (correct && word === correct) {
                this.correctWords.add(key);
            }
        }
    }

    // ─── Clue interaction ───
    public selectClue(clue: ClueItem, dir: 'across' | 'down') {
        if (this.wordFeedback !== 'none') return; // Block during feedback
        this.direction = dir;
        this.selectCell(clue.row, clue.col);
    }

    public isClueActive(clue: ClueItem, dir: string): boolean {
        return this.activeClueKey === `${clue.number}_${dir}`;
    }

    public get activeClue(): { number: number; clue: string; direction: string; length: number } | null {
        if (!this.activeClueKey) return null;
        const parts = this.activeClueKey.split('_');
        if (parts.length < 2) return null;
        const num = parseInt(parts[0]);
        const dir = parts.slice(1).join('_');
        const clues = dir === 'across' ? this.acrossClues : this.downClues;
        const found = clues.find(c => c.number === num);
        if (!found) return null;
        return { number: found.number, clue: found.clue, direction: dir === 'across' ? '→' : '↓', length: found.length };
    }

    public isClueCorrect(clue: ClueItem, dir: string): boolean {
        return this.correctWords.has(`${clue.number}_${dir}`);
    }

    public getClueFilledCount(clue: ClueItem, dir: string): number {
        const dr = dir === 'down' ? 1 : 0;
        const dc = dir === 'across' ? 1 : 0;
        let count = 0;
        for (let i = 0; i < clue.length; i++) {
            if (this.userGrid[clue.row + dr * i]?.[clue.col + dc * i]) count++;
        }
        return count;
    }

    // ─── Hints ───
    public async useHintLetter() {
        this.flushInput();
        if (this.selectedRow < 0 || this.selectedCol < 0) return;
        const clue = this.getActiveClueObj();
        if (!clue) return;
        const dir = this.activeClueKey.split('_').slice(1).join('_');
        const dr = dir === 'down' ? 1 : 0;
        const dc = dir === 'across' ? 1 : 0;

        const i = this.activeBoxIndex;
        if (i >= clue.length) return;
        const r = clue.row + dr * i;
        const c = clue.col + dc * i;
        const key = this.cellKey(r, c);
        if (this.revealedCells.has(key)) return;

        const answerKey = this.activeClueKey;
        const word = this.answers[answerKey];
        if (word && i < word.length) {
            this.userGrid[r][c] = word[i];
            this.revealedCells.add(key);
            this.hintsUsed++;
            this.updateLetterBoxes();
            await this.service.render();
        }
    }

    public async useHintWord() {
        this.flushInput();
        if (!this.activeClueKey) return;
        const word = this.answers[this.activeClueKey];
        if (!word) return;

        const clue = this.getActiveClueObj();
        if (!clue) return;
        const dir = this.activeClueKey.split('_').slice(1).join('_');
        const dr = dir === 'down' ? 1 : 0;
        const dc = dir === 'across' ? 1 : 0;
        for (let i = 0; i < clue.length; i++) {
            const r = clue.row + dr * i;
            const c = clue.col + dc * i;
            this.userGrid[r][c] = word[i];
            this.revealedCells.add(this.cellKey(r, c));
        }
        this.hintsUsed++;
        this.correctWords.add(this.activeClueKey);
        this.updateLetterBoxes();

        // Move to next word after hint
        if (this.correctWords.size >= this.totalWords) {
            this.stopTimer();
            await this.saveAndShowResult();
        } else {
            this.moveToNextWord();
        }
        await this.service.render();
    }

    // ─── Submit all ───
    public async submitAll() {
        this.flushInput();
        const allClues = [...this.acrossClues.map(c => ({...c, dir: 'across'})), ...this.downClues.map(c => ({...c, dir: 'down'}))];

        this.wrongWords = [];
        this.wrongWordKeys = new Set();
        for (const clue of allClues) {
            const key = `${clue.number}_${clue.dir}`;
            if (this.correctWords.has(key)) continue;

            const dr = clue.dir === 'down' ? 1 : 0;
            const dc = clue.dir === 'across' ? 1 : 0;
            let word = '';
            for (let i = 0; i < clue.length; i++) {
                word += this.userGrid[clue.row + dr * i]?.[clue.col + dc * i] || '';
            }

            const correct = this.answers[key];
            if (correct && word === correct) {
                this.correctWords.add(key);
            } else {
                this.wrongWordKeys.add(key);
                this.wrongWords.push({
                    korean: correct || '',
                    clue: clue.clue,
                    direction: clue.dir === 'across' ? '→' : '↓'
                });
            }
        }

        // All correct → save immediately
        if (this.wrongWords.length === 0) {
            this.stopTimer();
            await this.saveAndShowResult();
            return;
        }

        // Some wrong → show review (don't save yet)
        this.selectedRow = -1;
        this.selectedCol = -1;
        this.activeClueKey = '';
        this.letterBoxes = [];
        this.phase = 'review';
        await this.service.render();
    }

    public async fixWrongWords() {
        this.flushInput();
        // Clear wrong word cells and go back to playing
        const allClues = [...this.acrossClues.map(c => ({...c, dir: 'across'})), ...this.downClues.map(c => ({...c, dir: 'down'}))];
        for (const clue of allClues) {
            const key = `${clue.number}_${clue.dir}`;
            if (!this.wrongWordKeys.has(key)) continue;
            const dr = clue.dir === 'down' ? 1 : 0;
            const dc = clue.dir === 'across' ? 1 : 0;
            for (let i = 0; i < clue.length; i++) {
                const r = clue.row + dr * i;
                const c = clue.col + dc * i;
                if (!this.revealedCells.has(this.cellKey(r, c)) && !this.isCellCorrectWord(r, c)) {
                    this.userGrid[r][c] = '';
                }
            }
        }
        this.wrongWordKeys = new Set();
        this.phase = 'playing';
        this.selectFirstUnanswered();
        await this.service.render();
        this.focusInput();
    }

    public async finalizeResult() {
        this.stopTimer();
        await this.saveAndShowResult();
    }

    // ─── Result ───
    private async saveAndShowResult() {
        this.resultCorrect = this.correctWords.size;
        this.resultTotal = this.totalWords;
        this.phase = 'result';

        // 게임 결과 저장 + XP 지급
        try {
            const { code, data } = await wiz.call("save_result", {
                words_found: this.resultCorrect,
                total_words: this.resultTotal,
                hints_used: this.hintsUsed,
                time_seconds: this.timerSeconds,
                word_ids: JSON.stringify(this.puzzleWordIds)
            });
            if (code === 200 && data) {
                this.xpResult = data;
                this.showXpAnimation = true;
                if (data.level_up) {
                    setTimeout(() => { this.showLevelUp = true; this.service.render(); }, 1200);
                }
            }
        } catch (e) { }
        await this.service.render();
    }

    public async dismissLevelUp() {
        this.showLevelUp = false;
        await this.service.render();
    }

    public get resultPercent(): number {
        if (this.resultTotal === 0) return 0;
        return Math.round((this.resultCorrect / this.resultTotal) * 100);
    }

    public get resultStars(): number {
        if (this.hintsUsed === 0 && this.resultCorrect >= this.resultTotal) return 3;
        if (this.resultCorrect >= this.resultTotal) return 2;
        if (this.resultPercent >= 50) return 1;
        return 0;
    }

    public async retry() {
        await this.generatePuzzle();
    }

    public goBack() {
        if (this.crosswordMode) {
            this.service.href('/game');
        } else {
            this.service.href('/category/' + this.getLessonCategoryId());
        }
    }

    public goHome() {
        this.service.href('/dashboard');
    }

    private getLessonCategoryId(): string {
        return this.lessonId.split('_')[0] || '';
    }

    // ─── Grid size ───
    public get cellSize(): number {
        const maxWidth = Math.min(window.innerWidth - 32, 560);
        const maxHeight = window.innerHeight * 0.4;
        const dim = Math.max(this.rows, this.cols);
        const byWidth = Math.floor(maxWidth / dim);
        const byHeight = Math.floor(maxHeight / dim);
        return Math.max(22, Math.min(byWidth, byHeight, 36));
    }

    public get gridWidth(): number {
        return this.cols * this.cellSize;
    }

    public get fontSize(): number {
        return Math.max(12, this.cellSize * 0.45);
    }

    public get numberFontSize(): number {
        return Math.max(7, this.cellSize * 0.22);
    }
}

export default PageKorslovoCrosswordComponent;